import React from 'react';

import {Box} from '@mui/material';
import {motion} from 'framer-motion';
import {noop} from 'lodash';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon';

import {AppButton, AppButtonVariant} from '../AppButton';

import {CarouselItem} from '.';
import {
  DAMPING,
  DRAG_ELASTIC,
  EXTRA_ITEM_PERCENTAGE,
  FULL_PERCENTAGE,
  MASS,
  PADDING_RIGHT,
  STIFFNESS,
} from './models/constants';
import {CarouselProps, useActions} from './useActions';

import './styles';

export const Carousel: React.FC<CarouselProps> = ({
  items,
  itemsPerSlide,
  alwaysShowArrows = true,
  alwaysShowDots = false,
  deactivateDots = false,
  buttonText,
  extraItemPercentage = EXTRA_ITEM_PERCENTAGE,
  onClickItem = noop,
  setCurrentCustomSlide = noop,
}) => {
  const {
    x,
    isMobile,
    currentSlide,
    totalSlides,
    getTranslateX,
    getDragConstraints,
    shouldFitToSlide,
    shouldEnableSwipe,
    onClickDot,
    onClickNextSlide,
    onClickPrevSlide,
    onDragEnds,
    onSelectCarouselItem,
    onClickButton,
  } = useActions({
    items,
    itemsPerSlide,
    deactivateDots,
    extraItemPercentage,
    onClickItem,
    setCurrentCustomSlide,
  });

  return (
    <section className="carousel-wrapper">
      <div className="carousel-inner">
        <motion.div
          className="carousel-track"
          style={{x}}
          animate={{x: `${getTranslateX}%`}}
          transition={{
            type: 'spring',
            stiffness: STIFFNESS,
            damping: DAMPING,
            mass: MASS,
          }}
          drag={shouldEnableSwipe ? 'x' : false}
          dragConstraints={getDragConstraints}
          dragElastic={DRAG_ELASTIC}
          onDragEnd={(_event, info) => onDragEnds(info)}>
          {items.map((item, index) => (
            <Box
              key={index}
              style={{
                flex: `0 0 calc(${FULL_PERCENTAGE / (itemsPerSlide + extraItemPercentage)}% - ${index === items.length - 1 && items.length > itemsPerSlide ? PADDING_RIGHT : 0}px)`,
                paddingRight: !shouldFitToSlide
                  ? PADDING_RIGHT
                  : index === items.length - 1
                    ? 0
                    : PADDING_RIGHT,
              }}
              onClick={() => onSelectCarouselItem(item.id)}>
              <CarouselItem
                key={index}
                title={item.title}
                descriptionList={item.descriptionList}
                image={item.image}
                note={item.note}
              />
            </Box>
          ))}
        </motion.div>
      </div>

      {alwaysShowArrows && !isMobile && (
        <>
          {currentSlide > 0 && (
            <button
              className="carousel-prev-button"
              onClick={onClickPrevSlide}>
              <FuseSvgIcon size={16}>
                heroicons-outline:arrow-small-left
              </FuseSvgIcon>
            </button>
          )}
          {currentSlide < totalSlides - 1 && (
            <button
              className="carousel-next-button"
              onClick={onClickNextSlide}>
              <FuseSvgIcon size={16}>
                heroicons-outline:arrow-small-right
              </FuseSvgIcon>
            </button>
          )}
        </>
      )}

      {(alwaysShowDots || (!isMobile && totalSlides > 1)) && (
        <section
          className="carousel-dots"
          style={{marginBottom: shouldFitToSlide ? '-18px' : 'auto'}}>
          {Array.from({length: totalSlides}).map((_, index) => (
            <div
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => onClickDot(index)}
            />
          ))}
        </section>
      )}

      {buttonText && (
        <section className="carousel-button">
          <AppButton
            label={buttonText}
            onClick={onClickButton}
            variant={AppButtonVariant.onPrimary}
          />
        </section>
      )}
    </section>
  );
};
